/**
 * Keycloak realm-role extraction from the stored access token.
 *
 * The admin panel routes by role (RF-001/003): `admin` → `/admin/*`,
 * `merchant` → `/merchant/kds`. Roles live in the JWT `realm_access.roles`
 * claim (the backend's `KeycloakJwtConverter` reads the same claim). We only
 * decode the payload for routing/UX — every API call is still authorized
 * server-side, so a tampered client token gains nothing.
 */
import { useMemo } from 'react';
import { getAccessToken } from './tokens';
import { useAuth } from './useAuth';

interface JwtPayload {
  realm_access?: { roles?: string[] };
}

/** Base64URL-decodes and parses a JWT payload; null on any malformation. */
export function decodeJwtPayload(token: string): JwtPayload | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64)) as JwtPayload;
  } catch {
    return null;
  }
}

/** Realm roles from a token string (empty array when absent/invalid). */
export function rolesFromToken(token: string | null): string[] {
  if (!token) return [];
  return decodeJwtPayload(token)?.realm_access?.roles ?? [];
}

/** Realm roles of the current session. */
export const getCurrentRoles = (): string[] => rolesFromToken(getAccessToken());

export const currentUserHasRole = (role: string): boolean => getCurrentRoles().includes(role);

/** Reactive roles hook — recomputes when the login state flips. */
export function useRoles(): string[] {
  const isLoggedIn = useAuth((state) => state.isLoggedIn);
  return useMemo(() => (isLoggedIn ? getCurrentRoles() : []), [isLoggedIn]);
}
